<template>
    <!--
        An event Milo found on the web and proposes for Vsebina › Hotel › Okolica (docs/API.md
        §14.6.1): the staged picture with its credit (the organiser's page, or Milo's AI), the
        name, when and where, the distance and price, the description and the source page.
        While pending the picture may be removed, switched or made again before Potrdi.
    -->
    <div class="flex flex-col gap-2">
        <div v-if="showPicture" class="overflow-hidden rounded-md border border-default bg-elevated">
            <img :src="imageUrl" :alt="name" class="aspect-video w-full object-cover" @error="broken = imageUrl">
        </div>
        <p v-if="showPicture && source === MiloPictureSource.Source" class="text-xs text-muted">
            {{ $t('milo.event_picture_source') }}
            <a :href="credit" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">{{ hostOf(credit) }}</a>
        </p>
        <p v-else-if="showPicture" class="text-xs text-muted">{{ $t('milo.event_picture_ai') }}</p>
        <p v-else class="text-xs text-muted">{{ $t('milo.event_picture_later') }}</p>

        <div v-if="pending && actions.length" class="flex flex-wrap gap-1.5">
            <UButton
                v-for="action in actions"
                :key="action.value"
                :label="action.label"
                :icon="action.icon"
                color="neutral"
                variant="outline"
                size="xs"
                :loading="changing === action.value"
                :disabled="busy || !!changing"
                @click="emit('picture', action.value)"
            />
        </div>

        <div class="flex flex-col gap-0.5">
            <p class="text-sm font-medium text-highlighted">{{ name }}</p>
            <p v-if="meta" class="text-xs text-muted">{{ meta }}</p>
        </div>
        <p v-if="description" class="text-sm whitespace-pre-line text-default">{{ description }}</p>
        <p v-if="sourceUrl" class="text-xs text-muted">
            {{ $t('milo.event_source') }}
            <a :href="sourceUrl" target="_blank" rel="noopener noreferrer" class="break-all text-primary hover:underline">{{ sourceUrl }}</a>
        </p>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { MiloPictureAction, MiloPictureSource } from '@/enums/milo'
    import { formatDay } from '@/libs/dayjs'
    import { pickText } from '@/libs/localized'
    import type { LocalizedText } from '@/types/Common'
    import type { IMiloProposal } from '@/types/Milo'

    const props = defineProps<{
        proposal: IMiloProposal
        pending:  boolean
        busy?:    boolean
        /** The picture action under way, '' when none. */
        changing: string
    }>()

    const emit = defineEmits<(e: 'picture', action: MiloPictureAction) => void>()

    const { t, locale } = useI18n()

    /** The picture address that failed to load ('' = none) — a staged picture expires. */
    const broken = ref('')

    const text = (key: string): string => {
        const value = props.proposal.details[key]

        return typeof value === 'string' ? value : ''
    }

    const localized = (key: string): string => pickText(props.proposal.details[key] as LocalizedText | undefined, locale.value, 'sl')

    const imageUrl = computed((): string => text('image_url'))

    const source = computed((): string => text('image_source'))

    const credit = computed((): string => text('image_credit'))

    const sourceUrl = computed((): string => text('source_url'))

    const showPicture = computed((): boolean => !!imageUrl.value && broken.value !== imageUrl.value)

    const name = computed((): string => localized('name'))

    const description = computed((): string => localized('description'))

    const options = computed((): { source: boolean, ai: boolean } => {
        const value = props.proposal.details.image_options as { source?: boolean, ai?: boolean } | undefined

        return { source: !!value?.source, ai: !!value?.ai }
    })

    const meta = computed((): string => {
        const day  = formatDay(text('date'))
        const when = text('time') ? t('milo.event_at', { day, time: text('time') }) : day

        return [when, [text('venue'), text('town')].filter(Boolean).join(', '), localized('distance'), localized('price')]
            .filter(Boolean)
            .join(' · ')
    })

    /** What the card may do to the picture now. */
    const actions = computed((): { value: MiloPictureAction, label: string, icon: string }[] => {
        const list: { value: MiloPictureAction, label: string, icon: string }[] = []

        if (options.value.source && source.value !== MiloPictureSource.Source) {
            list.push({ value: MiloPictureAction.Source, label: t('milo.event_picture_use_source'), icon: 'i-lucide-globe' })
        }

        if (options.value.ai && source.value !== MiloPictureSource.Ai) {
            list.push({ value: MiloPictureAction.Ai, label: t('milo.event_picture_use_ai'), icon: 'i-lucide-sparkles' })
        }

        if (options.value.ai && source.value === MiloPictureSource.Ai) {
            list.push({ value: MiloPictureAction.Regenerate, label: t('milo.event_picture_regenerate'), icon: 'i-lucide-refresh-cw' })
        }

        if (imageUrl.value) {
            list.push({ value: MiloPictureAction.Remove, label: t('milo.event_picture_remove'), icon: 'i-lucide-image-off' })
        }

        return list
    })

    /** A page's host, for the credit line. */
    const hostOf = (address: string): string => {
        try {
            return new URL(address).host
        } catch {
            return address
        }
    }
</script>
